import {Box, Flex, HStack, StyleProps, VStack} from '@chakra-ui/react';
import {ReactNode} from 'react';
import {useTranslation} from 'react-i18next';
import {useBreakpointValue} from '@chakra-ui/react';
import {LigneProduitLayout} from '@/domains/liste/components/LigneProduitLayout';
import {HistoriqueAchatButton} from '@/domains/ligneProduit/HistoriqueAchatButton';
import {SuggestionQuantite} from '@/domains/ligneProduit/SuggestionQuantite';
import {PoidsVariableTooltip} from '@/domains/panier/PanierFound/ProduitPanier/PoidsVariableTooltip';
import {LabelDisponibilite} from '@/domains/catalogue/components/LabelDisponibilite';
import {ItemCatalogue2StateFF} from 'ff-common';
import {State} from '@synako/halitrek';
import {
  ArticlesCadencierSuggestionsAjoutesEventDesc,
  LigneDePanierDesc,
} from 'api-types/ffconnect-hal';
import {useHalState} from '@synako/haligator';
import {useClient} from '@/haligator-factory';
import {useSidePanelPanier} from '@/components/SidePanelPanier/SidePanelPanierContext';

type BuyBlockOptions = {
  somethingSubmitting?: boolean;
  afficher_bouton_liste?: boolean;
  texte?: string;
} & StyleProps;

/**
 * Layout commun pour une ligne de produit..
 */
export const LigneProduitCommune: React.FC<{
  itemState: ItemCatalogue2StateFF;
  articleBlock: ReactNode;
  prixBlock: ReactNode;
  uniteQuantiteBlock: (style?: StyleProps) => ReactNode;
  aPeser?: boolean;
  buyBlock: (opts?: BuyBlockOptions) => ReactNode;
  somethingSubmitting?: boolean;
  deleteBlock?: ReactNode;
  precommande?: boolean;
  ligneDePanier?: State<LigneDePanierDesc>;
  lastSuggestionEvent?: State<ArticlesCadencierSuggestionsAjoutesEventDesc>;
  afficher_suggestions?: boolean;
  footerBlock?: ReactNode;
  compactDesktop?: boolean;
}> = ({
  itemState,
  articleBlock,
  prixBlock,
  uniteQuantiteBlock,
  aPeser = false,
  buyBlock,
  somethingSubmitting = false,
  deleteBlock,
  precommande,
  ligneDePanier,
  lastSuggestionEvent,
  afficher_suggestions = false,
  footerBlock,
  compactDesktop = false,
}) => {
  const {t} = useTranslation();
  const clientNav = useClient();
  const {isOpen: isSidePanelOpen} = useSidePanelPanier();
  const buyTexte = useBreakpointValue({
    base: undefined,
    md: t('bouton_achat.buy_court'),
    '2xl': undefined,
  });

  // Select de quantité avec affichage du poids variable si nécessaire
  const selectBlock = (
    <Box
      mt={{base: '0', md: '15px'}}
      ml='0'
      mr={{base: '0px', md: '5px', xl: '10px'}}
      minW='fit-content'
    >
      {uniteQuantiteBlock(
        isSidePanelOpen
          ? {
              minW: {base: '245px', md: '120px', xl: '150px', '2xl': '245px'},
              w: {base: '245px', md: '120px', xl: '150px', '2xl': '245px'},
            }
          : compactDesktop
            ? {
                minW: {base: '245px', md: '170px', '2xl': '245px'},
                w: {base: '245px', md: '170px', '2xl': '245px'},
              }
            : {minW: '230px', w: '230px'},
      )}
      {aPeser && <PoidsVariableTooltip />}
    </Box>
  );

  // Bloc d'achat avec texte et dimensions adaptatifs selon l'ouverture du side panel
  const buyBlockRendered = buyBlock(
    isSidePanelOpen
      ? {
          somethingSubmitting,
          afficher_bouton_liste: true,
          texte: buyTexte,
          minW: {base: '0', md: '180px', '2xl': '280px'},
          w: {base: '100%', md: '180px', '2xl': '280px'},
        }
      : compactDesktop
        ? {
            somethingSubmitting,
            afficher_bouton_liste: true,
            texte: buyTexte,
            minW: {base: '0', md: '200px', lg: '205px', '2xl': '280px'},
            w: {base: '100%', md: '200px', lg: '205px', '2xl': '280px'},
          }
        : {
            somethingSubmitting,
            afficher_bouton_liste: true,
            minW: {base: '0', md: '280px'},
            w: {base: '100%', md: '280px'},
          },
  );

  const precommandeBlock = precommande ? (
    <LabelDisponibilite precommande={precommande} />
  ) : null;

  // Suggestions de quantité — fetch conditionné par la prop
  const {state: suggestionsState} = useHalState(
    afficher_suggestions ? clientNav.follow('suggestions') : undefined,
  );
  const hasSuggestion =
    afficher_suggestions &&
    suggestionsState?.data.suggestions.some(
      (s) => s.code_article === itemState.data.code,
    );

  const suggestionQuantiteBlock = hasSuggestion && (
    <SuggestionQuantite
      itemState={itemState}
      suggestionsState={suggestionsState}
      lastSuggestionEvent={lastSuggestionEvent}
      ligneDePanier={ligneDePanier}
    />
  );

  const historiqueAchatBlock = <HistoriqueAchatButton itemState={itemState} />;

  return (
    <LigneProduitLayout
      precommande={precommande}
      px='0'
      {...(footerBlock ? {flexDirection: 'column'} : {})}
    >
      {/* Desktop */}
      <VStack
        w='100%'
        position='relative'
        display={{base: 'none', md: 'flex'}}
      >
        <Flex
          w='100%'
          direction={{base: 'column', '2xl': 'row'}}
          gap={2}
        >
          <HStack
            flex='1'
            flexWrap='wrap'
            spacing={4}
            w='100%'
          >
            {precommandeBlock}
            <HStack
              flex='2'
              minW='260px'
              overflow='hidden'
            >
              <VStack
                minW={0}
                w='100%'
              >
                {articleBlock}
              </VStack>
            </HStack>
            <HStack
              alignItems='start'
              justifyContent='end'
              alignSelf='center'
              ml='auto'
              flex='1'
              gap={isSidePanelOpen || compactDesktop ? '1' : undefined}
              flexGrow={1}
            >
              {prixBlock}
              {selectBlock}
              <VStack alignItems='flex-start'>
                {buyBlockRendered}
                <HStack
                  justifyContent='left'
                  flexWrap='nowrap'
                  whiteSpace='nowrap'
                  gap='1'
                >
                  {historiqueAchatBlock}
                  {suggestionQuantiteBlock}
                </HStack>
              </VStack>
              <Box mt='15px'>{deleteBlock}</Box>
            </HStack>
          </HStack>
        </Flex>
      </VStack>
      {/* Mobile */}
      <VStack
        alignItems='stretch'
        w='100%'
        position='relative'
        display={{base: 'flex', md: 'none'}}
      >
        <Box
          position='absolute'
          top='0'
          right='0'
          zIndex='1'
        >
          {deleteBlock}
        </Box>
        <HStack>
          {precommandeBlock}
          {articleBlock}
        </HStack>
        <HStack
          justify='space-between'
          w='100%'
          mb='2'
        >
          {selectBlock}
          {prixBlock}
        </HStack>
        <HStack w='100%'>{buyBlockRendered}</HStack>
        <HStack
          w='100%'
          justifyContent='left'
        >
          {historiqueAchatBlock}
          {suggestionQuantiteBlock}
        </HStack>
      </VStack>
      {footerBlock && <Box w='100%'>{footerBlock}</Box>}
    </LigneProduitLayout>
  );
};
